import { reactive } from 'vue'
import TodayDeliveryApi, { SellerPolicy, SoldoutAction, todayDeliveryError } from '@/api/TodayDeliveryApi'
import { SERVER_MIRROR } from '@/constants/todayDeliveryMessages'
import { TODAY_POLICY_OFF, isTodayDeliveryPolicyActive } from '@/utils/todayDelivery'

/**
 * 오늘출발 시작 설정 (PM 2026-10-13).
 *
 * 정책을 쓰지 않는 셀러(OFF·정책 행 없음)는 서버가 새 지정(01→02)을 `TD_POLICY_OFF` 로 거절한다(엑셀 접수는 409).
 * 그래서 지정 진입점(옵션 관리 [추가]·[엑셀 일괄], 상품 리스트 [배송변경] > [오늘출발 지정], 옵션 편집기·옵션 없는 상품의
 * 배송타입 칸, 배송정책 관리 스위치)은 지정하기 **전에** `ensureTodayDeliveryActive` 로 정책을 먼저 켜게 한다.
 * 팝업(`TodayDeliveryStartDialog`)은 앱에 하나(`App.vue`)이고, 이 모듈의 상태(`todayDeliveryStartState`)를 그린다.
 */

export type TodayDeliveryStartOutcome = 'active' | 'cancelled' | 'blocked'

/** 스토어 가운데 이 모듈이 쓰는 것 — 셀러 번호·스낵바·전역 로딩 오버레이 */
export interface TodayDeliveryStartStore {
  getters: { sellerNo: number; loading?: boolean; loadingText?: string | null }
  commit: (type: string, payload?: unknown) => void
}

/** 팝업 1단계 초기값 — 배송정책 관리는 지금 폼 값을 넘긴다. 없으면 정책 응답 값 */
export interface TodayDeliveryStartPrefill {
  order_cutoff_time?: string | null
  default_soldout_action?: SoldoutAction | null
}

/**
 * 저장 작업이 시작된 시점(요청 세대·셀러) — 묶음·단계로 이어지는 저장이 하나를 나눠 쓴다(`captureTodayDeliveryScope`).
 * 그 뒤 다른 페이지로 옮겼거나 셀러가 바뀌었으면 이 작업은 정책 조회·팝업·재전송을 하지 않는다.
 */
export interface TodayDeliveryStartScope {
  generation: number
  sellerNo: number
}

export interface TodayDeliveryStartOptions {
  /** 팝업에서 시작했을 때 띄울 스낵바 — 진입점이 고른다(`START_SETUP.started*`). 없으면 띄우지 않는다 */
  successText?: string
  prefill?: TodayDeliveryStartPrefill
  /** 이 요청이 속한 저장 작업의 시작 시점 — 없으면 이 요청을 보낼 때 잡는다 */
  scope?: TodayDeliveryStartScope
}

/** 앱에 하나뿐인 시작 설정 팝업이 그리는 상태 */
export const todayDeliveryStartState = reactive({
  open: false,
  /** 팝업을 열 때 읽은 정책 — 동의 문안·마감시각 변경 가능 여부·출고지를 여기서 읽는다. 응답에 정책이 없으면 null */
  policy: null as SellerPolicy | null,
  prefill: null as TodayDeliveryStartPrefill | null,
  /** 열 때마다 올린다 — 팝업이 단계·입력을 처음부터 다시 그린다 */
  session: 0,
})

let settle: ((outcome: 'active' | 'cancelled') => void) | null = null
let inflight: Promise<TodayDeliveryStartOutcome> | null = null
// 요청 세대 — 다른 페이지로 옮기면 올린다(`cancelTodayDeliveryStart`). 정책 조회가 끝났을 때 세대가 바뀌었으면 팝업을 열지 않는다
let generation = 0

function openStartDialog(policy: SellerPolicy | null, prefill?: TodayDeliveryStartPrefill): Promise<'active' | 'cancelled'> {
  // 앞서 연 팝업을 기다리는 쪽이 남아 있으면 취소로 끝낸다(같은 팝업을 두 진입점이 나눠 쓰지 않는다)
  settle?.('cancelled')
  todayDeliveryStartState.policy = policy
  todayDeliveryStartState.prefill = prefill || null
  todayDeliveryStartState.session += 1
  todayDeliveryStartState.open = true
  return new Promise((resolve) => {
    settle = resolve
  })
}

/** 팝업을 닫는다 — [오늘출발 시작] 저장 성공이면 'active', [취소] 면 'cancelled' */
export function settleTodayDeliveryStart(outcome: 'active' | 'cancelled') {
  todayDeliveryStartState.open = false
  const resolve = settle
  settle = null
  resolve?.(outcome)
}

/**
 * 다른 페이지로 옮겼다 — 기다리는 시작 설정(정책 확인 중·팝업)을 모두 'cancelled' 로 끝낸다. 시작 설정 팝업이 라우트 이동마다 부른다.
 * 정책 조회가 늦게 끝나도 새 화면 위에 팝업을 열지 않고, 다음 호출은 새로 정책을 읽는다.
 */
export function cancelTodayDeliveryStart() {
  generation += 1
  inflight = null
  settleTodayDeliveryStart('cancelled')
}

async function runEnsure(store: TodayDeliveryStartStore, options: TodayDeliveryStartOptions): Promise<TodayDeliveryStartOutcome> {
  const requested = generation
  const sellerNo = store.getters.sellerNo
  let policy: SellerPolicy | null = null
  let readFailed = false
  try {
    const { data } = await new TodayDeliveryApi().getPolicy(sellerNo, { silentError: true })
    policy = data?.data || null
  } catch (error) {
    readFailed = true
  }
  // 기다리는 사이 다른 페이지로 옮겼거나 셀러가 바뀌었다 — 늦은 응답으로 새 화면에서 아무것도 하지 않는다
  if (requested !== generation || store.getters.sellerNo !== sellerNo) return 'cancelled'
  // 정책을 못 읽었다 — 막지 않는다. 서버가 저장 때 최종 판정하고, 거절하면 `sendWithTodayDeliveryStart` 가 다시 묻는다
  if (readFailed) return 'active'
  if (isTodayDeliveryPolicyActive(policy)) return 'active'
  if (policy?.program_status === 'SUSPENDED') {
    store.commit('showSnackbar', { text: SERVER_MIRROR.suspended, type: 'error' })
    return 'blocked'
  }
  if (policy?.participation_allowed === false) {
    store.commit('showSnackbar', { text: SERVER_MIRROR.participationLocked, type: 'error' })
    return 'blocked'
  }
  // 저장 도중(전역 로딩 오버레이가 떠 있을 때) 거절을 받아 여는 경우 — 오버레이(레이아웃, z-index 9999)가 팝업을 덮어 누를 수 없다.
  // 팝업이 떠 있는 동안만 내리고, 닫히면 같은 안내 문구로 되돌린다(저장 흐름이 끝날 때 원래대로 내린다)
  const loading = store.getters.loading ? { text: store.getters.loadingText || undefined } : null
  if (loading) store.commit('endLoading')
  const outcome = await openStartDialog(policy, options.prefill)
  // 팝업이 떠 있는 사이 다른 페이지로 옮겼다(취소로 닫혔다) — 새 화면에 로딩 오버레이를 되살리지 않는다
  if (loading && requested === generation) store.commit('startLoading', loading.text)
  if (outcome === 'active' && options.successText) store.commit('showSnackbar', { text: options.successText, type: 'success' })
  return outcome
}

/**
 * 오늘출발을 지정할 수 있는 상태인지 확인하고, 아니면 시작 설정 팝업을 연다.
 * - 'active': 정책이 활성(ON·참여 제한 없음)이거나 팝업에서 시작했다. 정책을 못 읽었을 때도 'active' 다(서버가 최종 판정).
 * - 'blocked': 운영자 중지·참여 제한 — 같은 안내를 스낵바로 띄우고 팝업은 열지 않는다.
 * - 'cancelled': 팝업을 [취소] 했다.
 * 같은 순간 여러 번 불려도(빠른 두 번 클릭·일괄 적용의 연속 변경) 팝업은 하나다 — 앞선 확인의 결과를 같이 쓴다.
 * 확인 중·팝업이 떠 있는 동안 다른 페이지로 옮기거나 셀러가 바뀌면 'cancelled' 다(`cancelTodayDeliveryStart`).
 */
export function ensureTodayDeliveryActive(
  store: TodayDeliveryStartStore,
  options: TodayDeliveryStartOptions = {}
): Promise<TodayDeliveryStartOutcome> {
  if (!inflight) {
    const run: Promise<TodayDeliveryStartOutcome> = runEnsure(store, options).finally(() => {
      // 그 사이 페이지를 옮겨 새 확인이 시작됐으면 그 확인은 건드리지 않는다
      if (inflight === run) inflight = null
    })
    inflight = run
  }
  return inflight
}

/** 저장 작업을 시작할 때 부른다 — 이어지는 요청(묶음·단계)에 `scope` 로 넘긴다 */
export function captureTodayDeliveryScope(store: TodayDeliveryStartStore): TodayDeliveryStartScope {
  return { generation, sellerNo: store.getters.sellerNo }
}

/** 쓰기 응답(`DeliveryCodeChangeResult`)에 '정책 미사용' 행 실패가 있는지 */
export function hasTodayPolicyOffRows(response: unknown): boolean {
  const failures = (response as { data?: { data?: { failures?: { reason_code?: string }[] } } } | null)?.data?.data?.failures
  return Array.isArray(failures) && failures.some((row) => row?.reason_code === TODAY_POLICY_OFF)
}

/**
 * 오늘출발 쓰기를 보내고, 서버가 '정책 미사용'(`TD_POLICY_OFF`)으로 거절하면 시작 설정을 띄운 뒤 **그 요청만 한 번** 더 보낸다.
 * 거절은 두 모양이다 — 행 실패(배송코드 지정·옵션 저장의 `failures[]`)와 요청 거절(엑셀 일괄 접수 409).
 * 시작하지 않았으면(취소·막힘) 첫 응답을 돌려주거나 첫 오류를 다시 던진다 — 호출부가 평소처럼 실패를 알린다.
 * 응답을 기다리는 사이 다른 페이지로 옮겼거나 셀러가 바뀌었으면 팝업 없이 첫 응답·오류를 그대로 돌려준다(새 화면에 팝업을 열지 않는다).
 * 기준 시점은 `options.scope`(저장 작업 시작) — 없으면 이 요청을 보낼 때다. 다시 보내기 직전에도 한 번 더 본다.
 */
export async function sendWithTodayDeliveryStart<T>(
  store: TodayDeliveryStartStore,
  send: () => Promise<T>,
  options: TodayDeliveryStartOptions = {}
): Promise<T> {
  // 저장 작업이 시작된 시점(없으면 지금) — 응답이 왔을 때와 다시 보내기 전에 비교한다
  const scope = options.scope || captureTodayDeliveryScope(store)
  const stillHere = () => scope.generation === generation && store.getters.sellerNo === scope.sellerNo
  let response: T
  try {
    response = await send()
  } catch (error) {
    if (todayDeliveryError(error).error_code !== TODAY_POLICY_OFF || !stillHere()) throw error
    if ((await ensureTodayDeliveryActive(store, options)) !== 'active' || !stillHere()) throw error
    return send()
  }
  if (!hasTodayPolicyOffRows(response) || !stillHere()) return response
  if ((await ensureTodayDeliveryActive(store, options)) !== 'active' || !stillHere()) return response
  return send()
}
